import React, { useEffect, useMemo, useRef, useState } from 'react'
import './GradientBarChart.css'
import { OtherText, ParagraphText } from 'styling/Typography'
import { Flex } from 'styling/GlobalStyles'

export const AreaChart = ({ data, height, showLabels, percentage }: IAreaChart) => {
    const maxValue = Math.max(...data.map((d) => d.value), 0)
    const gridLines = 5

    const containerRef = useRef<HTMLDivElement | null>(null)
    const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 })
    const [hoveredIdx, setHoveredIdx] = useState<number | null>(null)
    const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(null)

    useEffect(() => {
        if (!containerRef.current) return
        const element = containerRef.current
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const cr = entry.contentRect
                setContainerSize({ width: cr.width, height: cr.height })
            }
        })
        resizeObserver.observe(element)
        // initial
        const cr = element.getBoundingClientRect()
        setContainerSize({ width: cr.width, height: cr.height })
        return () => resizeObserver.disconnect()
    }, [])

    const paddingX = 8
    const paddingY = 4

    const points = useMemo(() => {
        const n = data.length
        const safeWidth = Math.max(containerSize.width - paddingX * 2, 0)
        const safeHeight = Math.max(containerSize.height - paddingY * 2, 0)
        if (n === 0 || safeWidth === 0 || safeHeight === 0 || maxValue === 0) return [] as { x: number; y: number }[]
        return data.map((item, i) => {
            const x = paddingX + (n === 1 ? safeWidth / 2 : (i * safeWidth) / (n - 1))
            const valueRatio = item.value / maxValue
            const y = paddingY + (1 - valueRatio) * safeHeight
            return { x, y }
        })
    }, [data, containerSize.width, containerSize.height, maxValue])

    // Smooth curve path using a simple Catmull-Rom to Bezier conversion
    const getSmoothPath = (pts: { x: number; y: number }[], tension = 1) => {
        if (pts.length === 0) return ''
        if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`
        const path: string[] = []
        path.push(`M ${pts[0].x} ${pts[0].y}`)
        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = i > 0 ? pts[i - 1] : pts[i]
            const p1 = pts[i]
            const p2 = pts[i + 1]
            const p3 = i !== pts.length - 2 ? pts[i + 2] : p2

            const cp1x = p1.x + ((p2.x - p0.x) / 6) * tension
            let cp1y = p1.y + ((p2.y - p0.y) / 6) * tension
            const cp2x = p2.x - ((p3.x - p1.x) / 6) * tension
            let cp2y = p2.y - ((p3.y - p1.y) / 6) * tension

            // Clamp control point Y values so the curve cannot overshoot
            // beyond the segment defined by p1 and p2. This prevents
            // unnatural dips/spikes (e.g. dropping below 0 before a big rise).
            const minY = Math.min(p1.y, p2.y)
            const maxY = Math.max(p1.y, p2.y)
            cp1y = Math.min(Math.max(cp1y, minY), maxY)
            cp2y = Math.min(Math.max(cp2y, minY), maxY)

            path.push(`C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`)
        }
        return path.join(' ')
    }

    const strokePath = useMemo(() => getSmoothPath(points), [points])

    const areaPath = useMemo(() => {
        if (!strokePath || points.length === 0) return ''
        // Close the area all the way to the bottom of the SVG so there is no visible gap
        const bottomY = Math.max(containerSize.height, 0)
        const firstX = points[0].x
        const lastX = points[points.length - 1].x
        // Build area by closing the smooth stroke to the bottom
        return `${strokePath} L ${lastX} ${bottomY} L ${firstX} ${bottomY} Z`
    }, [strokePath, points, containerSize.height])

    // Precompute segment bounds for hover capture
    const nearestPointIdxFromX = (x: number) => {
        if (points.length === 0) return null
        let minDist = Infinity
        let idx: number | null = null
        for (let i = 0; i < points.length; i++) {
            const d = Math.abs(points[i].x - x)
            if (d < minDist) {
                minDist = d
                idx = i
            }
        }
        return idx
    }

    return (
        <Flex column gap='10px' style={{ height: height ?? '100%' }}>
            <div className='chart-wrapper'>
                <div className='y-axis' style={{ display: 'flex', flexDirection: 'column-reverse', justifyContent: 'space-between' }}>
                    {Array.from({ length: gridLines + 1 }).map((_, idx) => (
                        <OtherText key={idx} style={{ opacity: '0.5' }}>
                            {Math.round((maxValue / gridLines) * idx)}
                            {percentage && '%'}
                        </OtherText>
                    ))}
                </div>

                <div className='chart-container' style={{ height: height ?? '100%', padding: 0 }} ref={containerRef}
                    onMouseMove={(e) => {
                        if (!containerRef.current) return
                        const rect = containerRef.current.getBoundingClientRect()
                        const x = e.clientX - rect.left
                        const idx = nearestPointIdxFromX(x)
                        if (idx === null) {
                            setHoveredIdx(null)
                            setHoverPos(null)
                            return
                        }
                        setHoveredIdx(idx)
                        setHoverPos({ x, y: points[idx]?.y ?? 0 })
                    }}
                    onMouseLeave={() => { setHoveredIdx(null); setHoverPos(null) }}
                >
                    {/* SVG Area */}
                    <svg width='100%' height='100%' viewBox={`0 0 ${Math.max(containerSize.width, 0)} ${Math.max(containerSize.height, 0)}`} preserveAspectRatio='none'>
                        <defs>
                            <linearGradient id='areaGradient' x1='0' y1='0' x2='0' y2={containerSize.height} gradientUnits='userSpaceOnUse'>
                                <stop offset='0%' stopColor='#ff9900' stopOpacity='1' />
                                <stop offset='100%' stopColor='#ff9900' stopOpacity='0.75' />
                            </linearGradient>
                        </defs>
                        {areaPath && <path d={areaPath} fill='url(#areaGradient)' stroke='none' />}
                        {strokePath && <path d={strokePath} fill='none' stroke='#ff9900' strokeWidth={2} strokeLinecap='round' strokeLinejoin='round' />}
                    </svg>
                    {hoveredIdx !== null && hoverPos && (
                        <div
                            className='tooltip'
                            style={{
                                position: 'absolute',
                                left: `${hoverPos.x}px`,
                                transform: 'translateX(-50%)',
                                top: `${Math.max(hoverPos.y - 36, 0)}px`,
                            }}
                        >
                            <span className='tooltip-name'>{data[hoveredIdx].name}</span>
                            <span className='tooltip-value'>{data[hoveredIdx].value}</span>
                        </div>
                    )}
                </div>
            </div>
            {showLabels && (
                <div className='labels-container'>
                    {data.map((l, i) => (
                        <ParagraphText key={i}>{l.name}</ParagraphText>
                    ))}
                </div>
            )}
        </Flex>
    )
}

interface IAreaChart {
    data: {
        value: number
        name: string
        color?: string
    }[]
    height?: number
    showLabels?: boolean
    percentage?: boolean
}


